/* eslint-disable jsx-a11y/label-has-associated-control */
import {
  Button,
  DialogTitle,
  Dialog,
  DialogContent,
  makeStyles,
  DialogActions,
  Box,
  Typography,
  Grid,
} from '@material-ui/core';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import 'cropperjs/dist/cropper.css';
import Cropper from 'cropperjs';
import CircularProgress from '@material-ui/core/CircularProgress';
import axios from 'axios';
import uploaderStyle from '../../theme/styles/ImageUploader';
import { imageSizeValidation } from '../../utils/ImageValidation';
import { getAWSUploadUrl } from '../../services/Assets';

const useStyles = makeStyles(uploaderStyle);
const supportedExts = ['jpeg', 'png', 'gif'];

const ChannelBannerImageUploader = ({ onSuccess, setUploading, title }) => {
  const classes = useStyles();
  const [cropper, setCropper] = useState(null);
  const [isFileUploading, setIsFileUploading] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState({
    show: false,
    type: '',
    message: '',
  });
  const [cropperModel, setCropperModel] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [cropperLoaded, setCropperLoaded] = useState(false);
  const [cropDetails, setCropDetails] = useState({
    width: 0,
    height: 0,
    isValid: true,
  });

  // Unique ID for this instance
  const previewId = `image-preview-${title.toLowerCase()}`;
  const fileInputId = `contained-button-file-${title.toLowerCase()}`;

  const onProgress = () => {
    if (!isFileUploading) {
      setIsFileUploading(true);
      setUploading(true);
    }
  };

  const onError = () => {
    setIsFileUploading(false);
    setUploading(false);
  };

  const onFinish = (signedUrl) => {
    const URL = signedUrl?.split('?')[0];
    onSuccess(URL);
    setIsFileUploading(false);
    setUploading(false);
  };

  const closeCropModel = () => {
    if (cropper) {
      cropper.destroy(); // Clean up cropper
    }
    setCropperModel(false);
    setCropper(null);
  };

  const uploadFile = async (data, blobData) => {
    try {
      await axios.put(data.signedUrl, blobData, {
        headers: { 'Content-Type': selectedFile.type },
        onUploadProgress: () => {
          onProgress();
        },
      });
      onFinish(data.signedUrl);
    } catch {
      onError();
    }
  };

  const getSignedUrl = (file, blobData) => {
    closeCropModel();
    setSnackbarMessage({
      message: '',
      type: '',
      show: false,
    });
    const params = {
      objectName: file.name,
      contentType: file.type,
      path: 'channels/images',
    };

    getAWSUploadUrl(params)
      .then((data) => {
        uploadFile(data, blobData);
      })
      .catch(() => {
        setIsFileUploading(false);
        setUploading(false);
      });
  };

  const initCropper = () => {
    const image = document.getElementById(previewId);
    if (image && !cropper) {
      const cropperInstance = new Cropper(image, {
        aspectRatio: 12 / 5,
        viewMode: 2,
        crop(e) {
          const width = parseFloat(e.detail.width).toFixed(0);
          const height = parseFloat(e.detail.height).toFixed(0);
          setCropDetails({
            width,
            height,
            isValid: width >= 480 && height >= 200,
          });
        },
      });
      setCropperLoaded(true);
      setCropper(cropperInstance);
    }
  };

  const readURL = async (evt) => {
    setSnackbarMessage({
      message: '',
      type: '',
      show: false,
    });
    const input = evt.target;
    if (input.files && input.files[0]) {
      const imageDimensions = await imageSizeValidation(input.files[0]);

      if (
        input.files[0].type.split('/')[0] === 'image' &&
        supportedExts.includes(input.files[0].type.split('/')[1]) &&
        imageDimensions === true
      ) {
        setSelectedFile(input.files[0]);
        const reader = new FileReader();
        reader.onload = (e) => {
          const previewImg = document.getElementById(previewId);
          if (previewImg) {
            previewImg.src = e.target.result;
          }
        };
        reader.readAsDataURL(input.files[0]);
        setCropperModel(true);
        setCropperLoaded(false);
        setTimeout(initCropper, 1000);
        input.value = '';
      } else {
        setSelectedFile(null);
        if (!supportedExts.includes(input.files[0].type.split('/')[1])) {
          setSnackbarMessage({
            message: `Sorry, ${
              input.files[0].name
            } is invalid, allowed extensions are: ${supportedExts.join(', ')}`,
            type: 'error',
            show: true,
          });
        } else {
          setSnackbarMessage({
            message: imageDimensions || `Please select image file only.`,
            type: 'error',
            show: true,
          });
        }
        setIsFileUploading(false);
        setUploading(false);
        input.value = '';
      }
    }
  };

  const getCroppedImageUrl = () => {
    const canvas = cropper.getCroppedCanvas();
    if (canvas.width >= 480 && canvas.height >= 200) {
      canvas.toBlob(
        (blob) => {
          getSignedUrl(selectedFile, blob);
        },
        selectedFile.type,
        1
      );
    } else {
      setSnackbarMessage({
        message: 'Cropped area is too small. Please select at least 480x200.',
        type: 'error',
        show: true,
      });
    }
  };

  // Clean up cropper on unmount
  React.useEffect(
    () => () => {
      if (cropper) {
        cropper.destroy();
      }
    },
    [cropper]
  );

  return (
    <div className={classes.profileUploaderContainer}>
      <Grid container spacing={1} style={{ marginBottom: 10 }}>
        <Grid item>
          <Button
            onClick={() => document.getElementById(fileInputId).click()}
            variant="outlined"
            title={snackbarMessage.message}
            style={{ marginTop: 10 }}
            size="small"
            disabled={isFileUploading}
          >
            Upload {title} Image
          </Button>
        </Grid>
        {snackbarMessage.show && (
          <Grid item xs={12}>
            <Typography style={{ color: '#f44336' }}>{snackbarMessage.message}</Typography>
          </Grid>
        )}
      </Grid>

      <div>
        <input id={fileInputId} accept="image/*" type="file" onChange={readURL} hidden />
      </div>

      {cropperModel && (
        <Dialog open onClose={closeCropModel} maxWidth="lg" fullWidth>
          <DialogTitle>Move, Scale and Select area for {title} image</DialogTitle>
          <DialogContent>
            <img id={previewId} src="#" alt="" hidden={!cropperLoaded} />
            {!cropperLoaded && <CircularProgress size={30} />}
          </DialogContent>
          <DialogActions>
            <Box display="flex" width="100%">
              <Box
                display="flex"
                flexDirection="row"
                flex={2}
                justifyContent="center"
                alignItems="center"
              >
                <Typography variant="body2">Minimum required resolution: 480x200.&nbsp;</Typography>
                <Typography
                  variant="body2"
                  style={{ color: `${!cropDetails.isValid ? 'red' : 'black'}` }}
                >
                  Your selected area: {cropDetails.width}x{cropDetails.height}.
                </Typography>
              </Box>
              <Button
                onClick={getCroppedImageUrl}
                color="primary"
                variant="contained"
                disableElevation
                className={classes.mr10}
                disabled={!cropDetails.isValid}
              >
                Save
              </Button>
              <Button onClick={closeCropModel} color="default" variant="contained" disableElevation>
                Cancel
              </Button>
            </Box>
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
};

export default ChannelBannerImageUploader;

ChannelBannerImageUploader.propTypes = {
  onSuccess: PropTypes.func.isRequired,
  setUploading: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
};
